import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useLang } from "@/i18n";
import { Megaphone, ChevronRight } from "lucide-react";

/**
 * A19-P6.3 — Internal promotional slot banner.
 *
 * Consumes `GET /api/referrals/ad-slots-for-me?placement=<placement>` and
 * renders a single contextual banner with a localized disclosure.
 *
 * Renders nothing when:
 *   - the call fails / returns `{active: false}`
 *   - the slot is missing required i18n keys
 *
 * Props:
 *   placement: "referrals_top" | "dashboard_home" | "dashboard_nav"
 *   onComposerCta?: () => void   // called when slot.cta_target === "composer"
 */
const VARIANT_STYLES = {
  emerald: {
    wrapper: "border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-emerald-50/40",
    rail:    "bg-emerald-500",
    tag:     "bg-emerald-100 text-emerald-900",
    cta:     "bg-emerald-600 hover:bg-emerald-700 text-white",
    icon:    "text-emerald-600",
  },
  violet: {
    wrapper: "border-violet-200 bg-gradient-to-br from-violet-50 via-white to-violet-50/40",
    rail:    "bg-violet-500",
    tag:     "bg-violet-100 text-violet-900",
    cta:     "bg-violet-600 hover:bg-violet-700 text-white",
    icon:    "text-violet-600",
  },
  sky: {
    wrapper: "border-sky-200 bg-gradient-to-br from-sky-50 via-white to-sky-50/40",
    rail:    "bg-sky-500",
    tag:     "bg-sky-100 text-sky-900",
    cta:     "bg-sky-600 hover:bg-sky-700 text-white",
    icon:    "text-sky-600",
  },
  amber: {
    wrapper: "border-amber-200 bg-gradient-to-br from-amber-50 via-white to-amber-50/40",
    rail:    "bg-amber-500",
    tag:     "bg-amber-100 text-amber-900",
    cta:     "bg-amber-600 hover:bg-amber-700 text-white",
    icon:    "text-amber-600",
  },
};

export const AdSlotBanner = ({ placement, onComposerCta }) => {
  const { t } = useLang();
  const navigate = useNavigate();
  const [slot, setSlot] = useState(null);
  // A19-P6.5 — guard against double-impression on the same render (e.g.
  // React StrictMode mounting twice in dev, or Suspense replays).
  const impressionFiredRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/referrals/ad-slots-for-me`, { params: { placement } })
      .then((r) => {
        if (cancelled) return;
        if (r.data?.active && r.data.slot) setSlot(r.data.slot);
      })
      .catch(() => { /* graceful: render nothing */ });
    return () => { cancelled = true; };
  }, [placement]);

  // A19-P6.5 — fire impression once per (slot_id, mount). The server
  // does its own 30-min dedupe per (user, slot) on top of this guard.
  useEffect(() => {
    if (!slot || !slot.slot_id) return;
    if (impressionFiredRef.current === slot.slot_id) return;
    impressionFiredRef.current = slot.slot_id;
    api
      .post(`/referrals/ad-slots/${encodeURIComponent(slot.slot_id)}/event`, {
        event_type: "impression",
        placement: slot.placement,
        campaign_id: slot.campaign_id || null,
      })
      .catch(() => { /* analytics must never break UX */ });
  }, [slot]);

  if (!slot) return null;

  // Resolve i18n keys; if any returns the raw key it's a missing
  // translation — hide the banner rather than render an ugly path.
  const title = t(slot.title_key);
  const body  = t(slot.body_key);
  const cta   = t(slot.cta_key);
  if (
    !title || title === slot.title_key ||
    !body  || body  === slot.body_key  ||
    !cta   || cta   === slot.cta_key
  ) return null;

  const variant = VARIANT_STYLES[slot.style_variant] || VARIANT_STYLES.emerald;
  const disclosure = t("referrals.promoSlots.internalNotice");

  const handleCta = () => {
    // A19-P6.5 — record click before navigating. Fire-and-forget; never
    // block navigation on the network call.
    try {
      api
        .post(`/referrals/ad-slots/${encodeURIComponent(slot.slot_id)}/event`, {
          event_type: "click",
          placement: slot.placement,
          campaign_id: slot.campaign_id || null,
        })
        .catch(() => {});
    } catch { /* no-op */ }
    switch (slot.cta_target) {
      case "composer":
        if (typeof onComposerCta === "function") return onComposerCta();
        navigate("/app/referrals");
        return;
      case "referrals_page":
        navigate("/app/referrals");
        return;
      case "questionnaires":
        navigate("/app");
        return;
      case "campaigns":
        navigate("/admin/referral-campaigns");
        return;
      default:
        // Unknown target — fall back to referrals page.
        navigate("/app/referrals");
    }
  };

  return (
    <div
      className={`relative overflow-hidden rounded-xl border ${variant.wrapper} p-5 shadow-sm`}
      data-testid={`ad-slot-${placement}`}
      data-slot-id={slot.slot_id}
    >
      <div className={`absolute inset-y-0 left-0 w-1 ${variant.rail}`} aria-hidden="true" />
      <div className="flex items-start gap-4 flex-wrap sm:flex-nowrap">
        <Megaphone className={`w-5 h-5 mt-0.5 shrink-0 ${variant.icon}`} aria-hidden="true" />
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span
              className={`inline-flex items-center rounded-full ${variant.tag} px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide`}
              data-testid={`ad-slot-${placement}-tag`}
            >
              {disclosure}
            </span>
          </div>
          <h3
            className="text-base font-semibold text-slate-900"
            data-testid={`ad-slot-${placement}-title`}
          >
            {title}
          </h3>
          <p
            className="text-sm text-slate-600 mt-0.5"
            data-testid={`ad-slot-${placement}-body`}
          >
            {body}
          </p>
        </div>
        <button
          type="button"
          onClick={handleCta}
          className={`inline-flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium shrink-0 ${variant.cta}`}
          data-testid={`ad-slot-${placement}-cta`}
        >
          {cta}
          <ChevronRight className="w-4 h-4 rtl:rotate-180" />
        </button>
      </div>
    </div>
  );
};

export default AdSlotBanner;
